import { BRAND_OG_IMAGE_URL, BRAND_PERSON_NAME, BRAND_SITE_HOST, BRAND_SITE_URL, BRAND_SOURCE_KEY, BRAND_STORAGE_PREFIX } from "@/lib/brandConfig";
import { HEADER_PROFILE_IMAGE_URL } from "@/config/cloudflare";
import { MEJORES_AMIGOS_CTA_URL } from "@/config/mejoresAmigosVideo";
import { getTelegramGroupByRegion } from "@/config/telegramGroups";
import { useRegion } from "@/hooks/useRegion";
import { useSEO } from "@/hooks/useSEO";
import { trackButtonClickGA } from "@/lib/analytics";
import { DATAFAST_GOALS, trackDataFastGoal } from "@/lib/datafastAnalytics";
import { trackPurchaseMeta } from "@/lib/metaPixelAnalytics";
import {
  getCurrentPurchaseData,
  initializePurchaseTracking,
  trackInstagramAccessClick,
  trackTelegramAccessClick,
  trackTimeOnThankYouPage,
} from "@/lib/purchaseTracking";
import { initializeUserTracking } from "@/lib/userIdentification";
import {
  ArrowRight,
  Heart,
  Instagram,
  Loader2,
  MessageCircle,
  Sparkles,
  UserPlus,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";

/**
 * Thank You Page - Unified for ALL languages (Spanish & English)
 * Single page for all regions: España, LATAM, US, Germany, France, rest of world
 *
 * OBLIGATORIO para Stripe Elements custom: /thank-you y /gracias son el destino
 * tras el pago cuando el producto da acceso a Telegram. Aquí se valida que el
 * usuario compró realmente (payment_intent/session_id + redirect_status) y se
 * le muestra el botón para ingresar al grupo/canal.
 *
 * SECURITY: Validates Stripe purchase before showing access buttons
 * Required URL params: session_id OR payment_intent OR checkout_session_id
 *
 * Language Detection:
 * - SPAIN, LATAM → Spanish content
 * - US, DE, FR, others → English content
 *
 * Shows 2 buttons (only if purchase is validated):
 * 1. Instagram Best Friends (Mejores Amigos)
 * 2. Telegram Group
 */

// Type for purchase validation state
type ValidationState = "loading" | "valid" | "invalid";

// =============================================================================
// Stripe / analytics metadata for purchases from this site (uses BRAND_* env)
// =============================================================================
const BRAND_CHECKOUT_METADATA = {
  source: BRAND_SOURCE_KEY,
  site: BRAND_SITE_HOST,
  brand: BRAND_PERSON_NAME,
} as const;

// Validate Stripe purchase parameters from URL
// Supports: Checkout Sessions, Payment Links, Payment Intents, Subscriptions
function validateStripePurchase(): {
  isValid: boolean;
  sessionId: string | null;
  paymentIntent: string | null;
  validationMethod: string;
} {
  const urlParams = new URLSearchParams(window.location.search);
  // Some redirects put params in hash (e.g. #payment_intent=pi_xxx&redirect_status=succeeded)
  const hash = window.location.hash.replace(/^#/, "");
  const hashParams = hash ? new URLSearchParams(hash) : null;
  const getParam = (key: string) =>
    urlParams.get(key) ?? hashParams?.get(key) ?? null;
  const fullUrl = window.location.href;

  // Log all URL params for debugging (only in development)
  if (import.meta.env.DEV) {
    console.log("[ThankYou] Validating purchase...");
    console.log("[ThankYou] Full URL:", fullUrl);
    console.log(
      "[ThankYou] URL Params:",
      Object.fromEntries(urlParams.entries())
    );
    console.log("[ThankYou] Referrer:", document.referrer);
  }

  // ==========================================================================
  // CAPTURE ALL POSSIBLE STRIPE PARAMETERS
  // ==========================================================================

  // Session IDs (Checkout Sessions, Payment Links)
  const sessionId =
    getParam("session_id") ||
    getParam("checkout_session_id") ||
    getParam("cs_id") ||
    getParam("cs");

  // Payment Intents (query or hash)
  const paymentIntent =
    getParam("payment_intent") ||
    getParam("pi") ||
    getParam("payment_intent_id");

  // Payment Intent Client Secret (from redirect flows)
  const paymentIntentClientSecret =
    getParam("payment_intent_client_secret") ||
    getParam("pi_client_secret");

  // Setup Intents (for subscriptions)
  const setupIntent =
    getParam("setup_intent") ||
    getParam("setup_intent_id") ||
    getParam("si");

  const setupIntentClientSecret =
    getParam("setup_intent_client_secret") ||
    getParam("si_client_secret");

  // Redirect Status (Stripe sends this on successful redirects; normalize for comparison)
  const redirectStatusRaw = getParam("redirect_status");
  const redirectStatus = redirectStatusRaw ? redirectStatusRaw.toLowerCase() : null;

  // Subscription ID (for subscription checkouts)
  const subscriptionId =
    getParam("subscription_id") ||
    getParam("sub_id") ||
    getParam("subscription");

  // Customer ID (sometimes passed)
  const customerId =
    getParam("customer_id") ||
    getParam("cus_id") ||
    getParam("customer");

  // Invoice ID (for invoice payments)
  const invoiceId =
    getParam("invoice_id") ||
    getParam("invoice") ||
    getParam("inv");

  // Payment Link ID (for tracking)
  const paymentLinkId =
    getParam("payment_link") ||
    getParam("plink");

  // ==========================================================================
  // VALIDATION METHODS - STRICT: Only allow access with proof of payment
  // ==========================================================================

  let isValid = false;
  let validationMethod = "none";

  // STRICT VALIDATION: Only accept when there's real proof of payment
  // Method 1: Valid Checkout Session ID (cs_test_xxx or cs_live_xxx) + redirect_status=succeeded
  // This confirms Stripe redirected after successful payment
  if (
    sessionId && 
    /^cs_(test|live)_[a-zA-Z0-9]+$/.test(sessionId) && 
    sessionId.length > 15 &&
    redirectStatus === "succeeded"
  ) {
    isValid = true;
    validationMethod = "checkout_session_with_success";
  }
  // Method 2: Valid Payment Intent (pi_xxx) with client secret
  // Client secret proves the payment was initiated and likely completed
  else if (
    paymentIntent && 
    /^pi_[a-zA-Z0-9]+$/.test(paymentIntent) && 
    paymentIntent.length > 10 &&
    paymentIntentClientSecret && 
    paymentIntentClientSecret.includes("_secret_")
  ) {
    isValid = true;
    validationMethod = "payment_intent_with_secret";
  }
  // Method 3: Valid Payment Intent (pi_xxx) + redirect_status=succeeded
  else if (
    paymentIntent && 
    /^pi_[a-zA-Z0-9]+$/.test(paymentIntent) && 
    paymentIntent.length > 10 &&
    redirectStatus === "succeeded"
  ) {
    isValid = true;
    validationMethod = "payment_intent_with_success";
  }
  // Method 4: Valid Subscription ID (sub_xxx) + redirect_status=succeeded
  // For subscription-based purchases
  else if (
    subscriptionId && 
    /^sub_[a-zA-Z0-9]+$/.test(subscriptionId) &&
    redirectStatus === "succeeded"
  ) {
    isValid = true;
    validationMethod = "subscription_with_success";
  }
  // Method 5: Setup Intent with success (for subscription setup)
  else if (
    setupIntent && 
    /^seti_[a-zA-Z0-9]+$/.test(setupIntent) &&
    redirectStatus === "succeeded"
  ) {
    isValid = true;
    validationMethod = "setup_intent_with_success";
  }
  // Method 6: Valid Invoice ID (in_xxx) + redirect_status=succeeded
  else if (
    invoiceId && 
    /^in_[a-zA-Z0-9]+$/.test(invoiceId) &&
    redirectStatus === "succeeded"
  ) {
    isValid = true;
    validationMethod = "invoice_with_success";
  }

  // SECURITY: Block all other access attempts
  // - No session_id without redirect_status
  // - No payment_intent without secret or success status
  // - No referrer-only validation
  // - No URL pattern matching without proper IDs

  // Log validation result (only in development)
  if (import.meta.env.DEV) {
    if (isValid) {
      console.log(`[ThankYou] ✅ Valid - Method: ${validationMethod}`);
      console.log("[ThankYou] Captured IDs:", {
        sessionId,
        paymentIntent,
        setupIntent,
        subscriptionId,
        customerId,
        invoiceId,
        paymentLinkId,
      });
    } else {
      console.warn("[ThankYou] ❌ No valid Stripe parameters found");
    }
  }

  return {
    isValid,
    sessionId,
    paymentIntent,
    validationMethod,
  };
}

// Content translations
const content = {
  es: {
    loading: "Verificando compra...",
    loadingSubtext: "Por favor espera un momento",
    noPurchaseTitle: "Gracias por visitar",
    noPurchaseMessage: "No encontramos datos de compra en esta sesión. Si acabas de completar el pago, espera unos segundos y recarga la página, o revisa el enlace de confirmación que te enviamos.",
    noPurchaseSubtext: "Si el problema continúa, contáctanos y te ayudamos.",
    returnHome: "Volver al Inicio",
    thankYou: "¡Muchas Gracias! 🎉",
    purchaseConfirmed: "Tu compra está confirmada",
    vipActive: "Acceso VIP Activado",
    welcomeTitle: "¡Bienvenido al Club VIP! 💎",
    welcomeMessage:
      "Ahora tienes <strong>acceso exclusivo</strong> a contenido premium. Haz clic en los botones de abajo para unirte:",
    registroButton: "Crear mi cuenta",
    registroSubtext: "Activá tu membresía y recibí tu acceso privado.",
    instagramButton: "Únete a Mejores Amigos",
    telegramButton: "Acceso al Grupo VIP",
    footerNote:
      "💎 Tu acceso VIP está activo. ¡Disfruta del contenido exclusivo!",
    seoTitle: `¡Gracias por tu compra! - Acceso Exclusivo | ${BRAND_PERSON_NAME}`,
    seoDescription:
      `🔥 Acceso VIP - ${BRAND_PERSON_NAME}. ¡Tu compra está confirmada! Accede inmediatamente a contenido exclusivo en Mejores Amigos de Instagram y nuestra comunidad privada exclusiva.`,
  },
  en: {
    loading: "Verifying purchase...",
    loadingSubtext: "Please wait a moment",
    noPurchaseTitle: "Thanks for visiting",
    noPurchaseMessage: "We didn't find purchase data in this session. If you just completed payment, wait a few seconds and refresh, or use the confirmation link we sent you.",
    noPurchaseSubtext: "If the problem continues, contact us and we'll help.",
    returnHome: "Return to Homepage",
    thankYou: "Thank You! 🎉",
    purchaseConfirmed: "Your purchase is confirmed",
    vipActive: "VIP Access Activated",
    welcomeTitle: "Welcome to the VIP Club! 💎",
    welcomeMessage:
      "You now have <strong>exclusive access</strong> to premium content! Click the buttons below to join:",
    registroButton: "Create my account",
    registroSubtext: "Activate your membership and receive your private access.",
    instagramButton: "Join Instagram Best Friends",
    telegramButton: "Access Exclusive Community",
    footerNote: "💎 Your VIP access is now active. Enjoy exclusive content!",
    seoTitle: `Thank You for Your Purchase! - Exclusive Access | ${BRAND_PERSON_NAME}`,
    seoDescription:
      `🔥 VIP Access - ${BRAND_PERSON_NAME}. Your purchase is confirmed! Get immediate access to exclusive content on Instagram Best Friends and our private exclusive community.`,
  },
};

const ThankYou = () => {
  const { region } = useRegion();
  const telegramGroupUrl = getTelegramGroupByRegion(region);

  // Determine language based on region
  const isSpanish = region === "SPAIN" || region === "LATAM";
  const lang = isSpanish ? "es" : "en";
  const t = content[lang];

  // Validation state
  const [validationState, setValidationState] =
    useState<ValidationState>("loading");
  const [stripeData, setStripeData] = useState<{
    sessionId: string | null;
    paymentIntent: string | null;
  }>({ sessionId: null, paymentIntent: null });

  // URL de registro post-pago → Clerk sign-up → completar-registro → pending-approval.
  // Apunta al alias /portal/registro: el usuario ve la URL coherente con el portal,
  // y CompleteRegistration enriquece el perfil con la session de Clerk (useUser).
  const registroUrl = useMemo(() => {
    const parts = ["from_standard=1", "plan_tier=standard"];
    if (stripeData.sessionId) parts.push(`session_id=${encodeURIComponent(stripeData.sessionId)}`);
    return `${MEMBERSHIP_ROUTE.PORTAL_REGISTRO}?${parts.join("&")}`;
  }, [stripeData.sessionId]);

  // Mejores Amigos: same link for all countries ($149.99)
  const instagramBestFriendsUrl = MEJORES_AMIGOS_CTA_URL;

  // SEO Configuration - Dynamic based on language
  const seoConfig = {
    title: t.seoTitle,
    description: t.seoDescription,
    keywords: isSpanish
      ? `acceso VIP, ${BRAND_PERSON_NAME}, Mejores Amigos Instagram, grupo privado Telegram, contenido exclusivo, membresía confirmada`
      : `VIP access, ${BRAND_PERSON_NAME}, Instagram Best Friends, private Telegram group, exclusive content, membership confirmed`,
    ogTitle: isSpanish
      ? `🎉 Acceso VIP Confirmado | ${BRAND_PERSON_NAME}`
      : `🎉 VIP Access Confirmed | ${BRAND_PERSON_NAME}`,
    ogDescription: isSpanish
      ? "💎 ¡Tu compra está confirmada! Únete a la comunidad VIP más exclusiva."
      : "💎 Your purchase is confirmed! Join the most exclusive VIP community.",
    canonical: `${BRAND_SITE_URL}/thank-you`,
    author: BRAND_PERSON_NAME,
    type: "website",
    locale: isSpanish ? "es_ES" : "en_US",
    ogImage: BRAND_OG_IMAGE_URL,
  };

  useSEO(seoConfig);

  // Validate purchase on mount
  useEffect(() => {
    // Small delay to show loading state (better UX)
    const timer = setTimeout(() => {
      const validation = validateStripePurchase();
      setStripeData({
        sessionId: validation.sessionId,
        paymentIntent: validation.paymentIntent,
      });

      if (validation.isValid) {
        setValidationState("valid");
      } else {
        setValidationState("invalid");

        // Log unauthorized access attempt
        console.warn(
          "[ThankYou] Unauthorized access attempt - no valid Stripe params",
          {
            url: window.location.href,
            timestamp: new Date().toISOString(),
          }
        );
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Enhanced purchase tracking (only if valid)
  useEffect(() => {
    if (validationState !== "valid") return;

    // DataFast: success page view (do NOT fire reserved goal name "payment")
    // Brand (BRAND_SOURCE_KEY) is auto-merged; revenue_type when membership path.
    trackDataFastGoal(DATAFAST_GOALS.checkoutSuccessPage, {
      page: "thank_you",
      language: isSpanish ? "es" : "en",
      revenue_type: "mrr",
    });

    // Initialize user tracking
    const userProfile = initializeUserTracking();

    // Initialize purchase tracking with Stripe data
    const purchaseData = initializePurchaseTracking("/thank-you");

    // Track time on page
    const cleanup = trackTimeOnThankYouPage();

    // Build complete purchase metadata for tracking
    const purchaseMetadata = {
      // {BRAND_PERSON_NAME} identification
      ...BRAND_CHECKOUT_METADATA,
      // User info
      userId: userProfile?.userId,
      region: userProfile?.region,
      country: userProfile?.country,
      // Stripe info
      stripeSessionId: stripeData.sessionId || purchaseData.sessionId,
      paymentIntent: stripeData.paymentIntent || purchaseData.paymentIntent,
      purchaseId: purchaseData.purchaseId,
      // Context
      language: isSpanish ? "es" : "en",
      timestamp: new Date().toISOString(),
      page: "/thank-you",
    };

    // Store metadata in sessionStorage for access tracking
    try {
      sessionStorage.setItem(`${BRAND_STORAGE_PREFIX}_purchase_metadata`, JSON.stringify(purchaseMetadata));
    } catch {
      // Ignore storage errors
    }

    // Meta Pixel Purchase event (once per purchase, prevent duplicate on reload)
    const purchaseId = stripeData.paymentIntent ?? stripeData.sessionId ?? purchaseData.purchaseId;
    if (purchaseId) {
      const metaTrackKey = `meta_purchase_tracked_${purchaseId}`;
      try {
        if (!sessionStorage.getItem(metaTrackKey)) {
          trackPurchaseMeta({
            value: 149.99,
            currency: "USD",
            content_ids: [purchaseId],
            content_name: "VIP Access - Telegram & Instagram Best Friends",
            content_type: "product",
            num_items: 1,
            // Shared dedup key with the server-side CAPI Purchase event fired
            // from the Stripe webhook (`api/webhooks/stripe.ts` →
            // `sendMetaCapiPurchase`). Using `purchaseId` (Stripe session.id
            // when available, else payment_intent) keeps both sides aligned
            // without needing extra plumbing.
            eventID: purchaseId,
          });
          sessionStorage.setItem(metaTrackKey, "1");
        }
      } catch {
        // Ignore Meta Pixel errors (e.g. adblocker)
      }
    }

    // Log purchase details in development
    if (import.meta.env.DEV) {
      console.log("👤 User Profile:", userProfile);
      console.log("🛒 Purchase Data:", purchaseData);
      console.log(`🏷️ ${BRAND_PERSON_NAME} Metadata:`, BRAND_CHECKOUT_METADATA);
      console.log("📊 Full Purchase Metadata:", purchaseMetadata);
    }

    return cleanup;
  }, [validationState, region, isSpanish, stripeData]);

  // Loading state
  if (validationState === "loading") {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center space-y-8">
          <div className="relative mx-auto w-16 h-16">
            <Loader2 className="w-16 h-16 text-white/40 animate-spin" strokeWidth={1} />
          </div>
          <div className="space-y-3">
            <p className="text-xl font-light tracking-wide text-white">
              {t.loading}
            </p>
            <p className="text-gray-500 text-sm font-light tracking-wider">
              {t.loadingSubtext}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Sin datos de compra válidos: página existe, mensaje claro (no 404)
  if (validationState === "invalid") {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center max-w-md space-y-8">
          <div className="space-y-4">
            <h1 className="text-3xl md:text-4xl font-light tracking-tight text-white/90">
              {t.noPurchaseTitle}
            </h1>
            <div className="w-16 h-[1px] bg-white/20 mx-auto"></div>
            <p className="text-lg font-light tracking-wide text-gray-400">
              {t.noPurchaseMessage}
            </p>
            <p className="text-sm font-light text-gray-500">
              {t.noPurchaseSubtext}
            </p>
          </div>

          <a
            href="/"
            className="inline-flex items-center justify-center px-10 py-4 bg-white text-black hover:bg-gray-100 rounded-full font-medium tracking-wide ui-transition duration-200 transform hover:scale-105"
          >
            {t.returnHome}
          </a>
        </div>
      </div>
    );
  }

  // Valid purchase - Show access content (Elegant Black & White Design)
  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Sophisticated Background Pattern */}
      <div className="absolute inset-0">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-black to-zinc-950"></div>
        
        {/* Elegant grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        ></div>

        {/* Radial gradient spotlight */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-white/[0.02] rounded-full blur-[100px]"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-2xl mx-auto px-6 py-20">
        {/* Header with minimalist design */}
        <div className="text-center mb-16">
          {/* Profile Image */}
          <div className="mb-10 flex justify-center">
            <div className="relative">
              <img
                src={HEADER_PROFILE_IMAGE_URL}
                alt={BRAND_PERSON_NAME}
                className="w-28 h-28 rounded-full border-2 border-white/20 shadow-2xl object-cover"
              />
              {/* Subtle glow */}
              <div className="absolute inset-0 rounded-full bg-white/5 blur-xl"></div>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-5xl md:text-6xl font-light mb-6 tracking-tight">
            {t.thankYou}
          </h1>

          {/* Separator line */}
          <div className="w-16 h-[1px] bg-white/30 mx-auto mb-6"></div>

          {/* Subtitle */}
          <p className="text-lg text-gray-400 font-light tracking-wide mb-4">
            {t.purchaseConfirmed}
          </p>

          {/* VIP Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-white/10 rounded-full bg-white/[0.02] backdrop-blur-sm">
            <Heart className="w-4 h-4 text-white" />
            <span className="text-xs uppercase tracking-widest text-gray-300 font-light">
              {t.vipActive}
            </span>
            <Heart className="w-4 h-4 text-white" />
          </div>
        </div>

        {/* Welcome Card */}
        <div className="border border-white/10 rounded-3xl p-10 mb-10 bg-white/[0.02] backdrop-blur-sm">
          <div className="flex items-start gap-5">
            <Sparkles className="w-6 h-6 text-white flex-shrink-0 mt-1 opacity-60" />
            <div>
              <h2 className="text-2xl font-light mb-4 tracking-tight">
                {t.welcomeTitle}
              </h2>
              <p
                className="text-gray-400 leading-relaxed font-light"
                dangerouslySetInnerHTML={{ __html: t.welcomeMessage }}
              />
            </div>
          </div>
        </div>

        {/* Access Buttons - Minimalist Black & White */}
        <div className="space-y-5">
          {/* ── Registro Clerk (acción principal) ── */}
          <div className="border border-white/10 rounded-2xl p-6 bg-white/[0.02] space-y-4">
            <div className="flex items-center gap-3">
              <UserPlus className="w-5 h-5 text-white opacity-60 flex-shrink-0" />
              <p className="text-gray-300 font-light leading-relaxed text-sm">
                {t.registroSubtext}
              </p>
            </div>
            <a
              href={registroUrl}
              className="group relative w-full flex items-center justify-center gap-3 px-10 py-5 bg-white text-black hover:bg-gray-100 rounded-full ui-transition duration-200 transform hover:scale-[1.02] focus:outline-none overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              <UserPlus className="w-5 h-5 relative z-10" />
              <span className="text-base font-medium tracking-wide relative z-10">
                {t.registroButton}
              </span>
              <ArrowRight className="w-4 h-4 relative z-10" />
            </a>
            <p className="text-xs text-gray-500 text-center font-light">
              {t.registroSubtext}
            </p>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-[1px] bg-white/10" />
            <span className="text-xs text-gray-600 font-light tracking-widest uppercase">o también</span>
            <div className="flex-1 h-[1px] bg-white/10" />
          </div>

          {/* Instagram Best Friends Button */}
          <a
            href={instagramBestFriendsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              // Track Instagram access
              trackInstagramAccessClick(
                isSpanish ? "mejores_amigos" : "best_friends"
              );

              // Track with GA
              import("@/lib/userIdentification").then(
                ({ getCurrentUserProfile, updateConversionStage }) => {
                  const userProfile = getCurrentUserProfile();
                  const purchaseData = getCurrentPurchaseData();
                  updateConversionStage("converted");

                  if (userProfile) {
                    trackButtonClickGA(
                      isSpanish
                        ? "Mejores Amigos Instagram - Español"
                        : "Instagram Best Friends - English",
                      "instagram-best-friends",
                      isSpanish ? "spanish_template" : "english_template",
                      "thank_you_page",
                      userProfile.country,
                      "ThankYouPage"
                    );
                  }

                  // Log conversion in development
                  if (import.meta.env.DEV) {
                    console.log("✅ Instagram Access Clicked:", {
                      ...BRAND_CHECKOUT_METADATA,
                      userId: userProfile?.userId,
                      purchaseId: purchaseData?.purchaseId,
                      stripeSessionId: stripeData.sessionId,
                      service: isSpanish ? "mejores_amigos" : "best_friends",
                      language: isSpanish ? "Spanish" : "English",
                      timestamp: new Date().toISOString(),
                    });
                  }
                }
              );
            }}
            className="group relative w-full flex items-center justify-center gap-3 px-10 py-6 bg-white text-black hover:bg-gray-100 rounded-full ui-transition duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-black overflow-hidden"
          >
            {/* Animated background on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            
            <Instagram className="w-5 h-5 relative z-10" />
            <span className="text-base font-medium tracking-wide relative z-10">
              {t.instagramButton}
            </span>
          </a>

          {/* Telegram Group Button */}
          <a
            href={telegramGroupUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              // Track Telegram access
              trackTelegramAccessClick(isSpanish ? "spanish" : "english");

              // Track with GA
              import("@/lib/userIdentification").then(
                ({ getCurrentUserProfile, updateConversionStage }) => {
                  const userProfile = getCurrentUserProfile();
                  const purchaseData = getCurrentPurchaseData();
                  updateConversionStage("converted");

                  if (userProfile) {
                    trackButtonClickGA(
                      isSpanish
                        ? "Grupo VIP Telegram - Español"
                        : "Telegram VIP Group - English",
                      "telegram-access",
                      isSpanish ? "spanish_template" : "english_template",
                      "thank_you_page",
                      userProfile.country,
                      "ThankYouPage"
                    );
                  }

                  // Log conversion in development
                  if (import.meta.env.DEV) {
                    console.log("✅ Telegram Access Clicked:", {
                      ...BRAND_CHECKOUT_METADATA,
                      userId: userProfile?.userId,
                      purchaseId: purchaseData?.purchaseId,
                      stripeSessionId: stripeData.sessionId,
                      group: isSpanish ? "spanish" : "english",
                      language: isSpanish ? "Spanish" : "English",
                      timestamp: new Date().toISOString(),
                    });
                  }
                }
              );
            }}
            className="group relative w-full flex items-center justify-center gap-3 px-10 py-6 border-2 border-white/20 text-white hover:bg-white hover:text-black hover:border-white rounded-full ui-transition duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-black overflow-hidden"
          >
            {/* Animated background on hover */}
            <div className="absolute inset-0 bg-white scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            
            <MessageCircle className="w-5 h-5 relative z-10 transition-colors duration-500" />
            <span className="text-base font-medium tracking-wide relative z-10 transition-colors duration-500">
              {t.telegramButton}
            </span>
          </a>
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center">
          <div className="w-8 h-[1px] bg-white/10 mx-auto mb-6"></div>
          <p className="text-sm text-gray-500 font-light tracking-wide">
            {t.footerNote}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
